import { formatDate, formatDateTime, formatMoney } from '@persona/shared';
import { Alert, Button, Card, Skeleton, StatusPill } from '@persona/ui';
import { useEffect, useState } from 'react';
import { adminApi, ApiError, type AdminClientDetail } from '../api';

const MEMBERSHIP_STATUS: Record<string, { label: string; tone: 'success' | 'warning' | 'neutral' }> = {
  active: { label: 'Активно', tone: 'success' },
  frozen: { label: 'Заморожено', tone: 'warning' },
  not_active: { label: 'Не активировано', tone: 'warning' },
  locked: { label: 'Заблокировано', tone: 'warning' },
  closed: { label: 'Закрыто', tone: 'neutral' },
};

const CONSENT_LABELS: Record<string, string> = {
  personal_data: 'Обработка персональных данных',
  offer: 'Публичная оферта',
  recurrent: 'Периодические списания',
  marketing: 'Рекламная рассылка',
};

/**
 * Карточка клиента.
 *
 * Собирается из двух источников: кабинет знает о регистрации и своих платежах,
 * 1С — о сроках членства и покупках на ресепшене. Показывать только одно
 * означало бы дать администратору неполную картину.
 */
export function ClientDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [client, setClient] = useState<AdminClientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setClient(null);
    setError(null);
    adminApi
      .clientDetail(id)
      .then(setClient)
      .catch((cause: unknown) => setError(cause instanceof ApiError ? cause.message : 'Не удалось загрузить карточку'));
  }, [id]);

  if (error) {
    return (
      <div>
        <Button variant="secondary" size="sm" onClick={onBack}>
          Назад к списку
        </Button>
        <Alert tone="danger" className="mt-6">
          {error}
        </Alert>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-40 w-full max-w-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Button variant="secondary" size="sm" onClick={onBack}>
          Назад к списку
        </Button>
      </div>

      <header>
        <h1 className="font-display text-2xl font-semibold">{client.fullName ?? 'Имя не заполнено'}</h1>
        <p className="mt-1 text-secondary">{client.phone}</p>
      </header>

      {client.onecError && <Alert tone="warning">{client.onecError}</Alert>}

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <h2 className="font-display text-lg font-semibold">Данные</h2>
          <dl className="mt-4 flex flex-col gap-2 text-sm">
            <Row label="Электронная почта" value={client.email ?? '—'} />
            <Row label="ID в 1С" value={client.onecClientId ?? '—'} mono />
            <Row label="Регистрация в кабинете" value={formatDate(client.registeredAt)} />
            <Row label="Последний вход" value={client.lastLoginAt ? formatDateTime(client.lastLoginAt) : '—'} />
          </dl>
        </Card>

        <Card>
          <h2 className="font-display text-lg font-semibold">Согласия</h2>
          {client.consents.length === 0 ? (
            <p className="mt-3 text-sm text-muted">Согласий не зафиксировано</p>
          ) : (
            <ul className="mt-4 flex flex-col gap-2 text-sm">
              {client.consents.map((consent) => (
                <li key={`${consent.kind}-${consent.docVersion}`} className="flex justify-between gap-4">
                  <span>{CONSENT_LABELS[consent.kind] ?? consent.kind}</span>
                  <span className="text-right text-muted">
                    {formatDate(consent.acceptedAt)} · ред. {consent.docVersion}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <section>
        <h2 className="font-display text-lg font-semibold">Членства по данным 1С</h2>
        <p className="mt-1 text-sm text-secondary">Именно эти сроки определяют доступ в клуб.</p>

        {client.memberships.length === 0 ? (
          <Card className="mt-4">
            <p className="text-sm text-muted">Членств не найдено</p>
          </Card>
        ) : (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {client.memberships.map((membership, index) => {
              const status = MEMBERSHIP_STATUS[membership.status] ?? { label: membership.status, tone: 'neutral' as const };
              return (
                <Card key={`${membership.title}-${index}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-medium">{membership.title}</h3>
                    <StatusPill tone={status.tone}>{status.label}</StatusPill>
                  </div>
                  <dl className="mt-4 flex flex-col gap-2 text-sm">
                    <Row label="Действует до" value={membership.endDate ? formatDate(membership.endDate) : '—'} />
                    <Row label="Автопродление" value={membership.isRecurrent ? 'Да' : 'Нет'} />
                    {membership.isRecurrent && (
                      <>
                        <Row
                          label="Договор"
                          value={membership.contractStatus === 'opened' ? 'Действует' : 'Закрыт'}
                        />
                        <Row
                          label="Следующее списание"
                          value={
                            membership.nextPaymentDate
                              ? `${formatDate(membership.nextPaymentDate)}${
                                  membership.nextPaymentAmountMinor
                                    ? ` · ${formatMoney(membership.nextPaymentAmountMinor)}`
                                    : ''
                                }`
                              : '—'
                          }
                        />
                      </>
                    )}
                  </dl>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-lg font-semibold">Покупки в 1С</h2>
        <p className="mt-1 text-sm text-secondary">Включая оформленные на ресепшене.</p>

        {client.purchases.length === 0 ? (
          <Card className="mt-4">
            <p className="text-sm text-muted">Покупок не найдено</p>
          </Card>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--border-strong)] text-left">
                  <th className="px-3 pb-2 text-xs font-medium tracking-wide text-muted uppercase">Дата</th>
                  <th className="px-3 pb-2 text-xs font-medium tracking-wide text-muted uppercase">Состав</th>
                  <th className="px-3 pb-2 text-xs font-medium tracking-wide text-muted uppercase">Сумма</th>
                  <th className="px-3 pb-2 text-xs font-medium tracking-wide text-muted uppercase">Долг</th>
                </tr>
              </thead>
              <tbody>
                {client.purchases.map((purchase) => (
                  <tr key={purchase.id} className="border-b border-[var(--border-subtle)] align-top">
                    <td className="px-3 py-3 whitespace-nowrap">{formatDateTime(purchase.datetime)}</td>
                    <td className="px-3 py-3">{purchase.items.join(', ') || '—'}</td>
                    <td className="px-3 py-3 font-medium whitespace-nowrap">{formatMoney(purchase.totalMinor)}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {purchase.debtMinor > 0 ? (
                        <span className="text-danger-500">{formatMoney(purchase.debtMinor)}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-lg font-semibold">Оплаты через кабинет</h2>

        {client.payments.length === 0 ? (
          <Card className="mt-4">
            <p className="text-sm text-muted">Через кабинет этот клиент ещё не платил</p>
          </Card>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--border-strong)] text-left">
                  <th className="px-3 pb-2 text-xs font-medium tracking-wide text-muted uppercase">Дата</th>
                  <th className="px-3 pb-2 text-xs font-medium tracking-wide text-muted uppercase">Сумма</th>
                  <th className="px-3 pb-2 text-xs font-medium tracking-wide text-muted uppercase">Статус</th>
                  <th className="px-3 pb-2 text-xs font-medium tracking-wide text-muted uppercase">В 1С</th>
                </tr>
              </thead>
              <tbody>
                {client.payments.map((payment) => (
                  <tr key={payment.id} className="border-b border-[var(--border-subtle)]">
                    <td className="px-3 py-3 whitespace-nowrap">{formatDateTime(payment.createdAt)}</td>
                    <td className="px-3 py-3 font-medium whitespace-nowrap">{formatMoney(payment.amountMinor)}</td>
                    <td className="px-3 py-3">{payment.status}</td>
                    <td className="px-3 py-3">
                      {payment.onecPostedAt ? 'зафиксирован' : <span className="text-danger-500">нет</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className={`text-right ${mono ? 'font-mono text-xs break-all' : 'font-medium'}`}>{value}</dd>
    </div>
  );
}
